import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';
import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.kiera.lustre');
import { OnInit, OnDestroy } from '@angular/core';
import { Service } from 'src/libs/portal/season/service';

@Component({
 standalone: false,
 selector: 'wiz-page-kiera-lustre',
template: templateSource || '',
    styles: [`

/* file: /opt/app/project/main/.kiera-build/src/app/page.kiera.lustre/view.scss */
:host {
  display: block;
  height: 100%;
}`],
})
export class PageKieraLustreComponent implements OnInit, OnDestroy {
    // Tab
    public tab: string = 'dashboard';

    // Config
    public config: any = { mgs_ip: '', mgs_user: 'root', mgs_password: '', fs_name: '', mount_point: '/mnt/lustre', mount_options: '' };
    public configSaved: boolean = false;
    public localStatus: any = null;
    public localLoading: boolean = false;
    public readonly commandExample: string = [
        'lctl get_param mdt.*.state',
        'lctl get_param obdfilter.*.stats',
        'lctl list_param osc.*.active',
        'mount -t lustre <MGS_IP>@tcp:/<FS_NAME> /mnt/lustre',
    ].join('\n');

    // Dashboard
    public fsStatus: any = null;
    public dashLoading: boolean = false;

    // Servers (OST/MDT)
    public servers: any[] = [];
    public serversLoading: boolean = false;

    // Clients
    public clients: any[] = [];
    public clientsLoading: boolean = false;
    public selectAll: boolean = false;

    // Bulk action
    public bulkAction: string = '';
    public bulkRunning: boolean = false;
    public bulkResults: any[] = [];

    private refreshTimer: any = null;

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        await this.loadConfig();
        await this.loadLocalStatus();
        if (this.config.mgs_ip) {
            await this.loadDashboard();
        }
        await this.service.render();
    }

    ngOnDestroy() {
        if (this.refreshTimer) clearInterval(this.refreshTimer);
    }

    public async switchTab(tab: string) {
        this.tab = tab;
        if (tab === 'dashboard' && this.config.mgs_ip) await this.loadDashboard();
        if (tab === 'servers' && this.config.mgs_ip) await this.loadServers();
        if (tab === 'clients') await this.loadClients();
        if (tab === 'settings') await this.loadLocalStatus();
        await this.service.render();
    }

    // =================== Config ===================
    public async loadConfig() {
        const { code, data } = await wiz.call("load_config");
        if (code === 200 && data) {
            Object.assign(this.config, data);
        }
    }

    public async saveConfig() {
        const { code } = await wiz.call("save_config", this.config);
        if (code === 200) {
            this.configSaved = true;
            setTimeout(() => { this.configSaved = false; this.service.render(); }, 2000);
        }
        await this.service.render();
    }

    public async loadLocalStatus() {
        this.localLoading = true;
        await this.service.render();
        const { code, data } = await wiz.call("local_status");
        this.localLoading = false;
        if (code === 200) {
            this.localStatus = data;
        }
        await this.service.render();
    }

    public localStatusBadge(status: string): string {
        if (status === 'installed') return 'bg-emerald-100 text-emerald-700';
        if (status === 'installable') return 'bg-sky-100 text-sky-700';
        if (status === 'blocked') return 'bg-rose-100 text-rose-700';
        return 'bg-zinc-100 text-zinc-600';
    }

    // =================== Dashboard ===================
    public async loadDashboard() {
        this.dashLoading = true;
        await this.service.render();
        const { code, data } = await wiz.call("fs_overview");
        this.dashLoading = false;
        if (code === 200) {
            this.fsStatus = data;
        }
        await this.service.render();
    }

    // =================== Servers ===================
    public async loadServers() {
        this.serversLoading = true;
        await this.service.render();
        const { code, data } = await wiz.call("server_status");
        this.serversLoading = false;
        if (code === 200) {
            this.servers = data || [];
        }
        await this.service.render();
    }

    // =================== Clients ===================
    public async loadClients() {
        this.clientsLoading = true;
        await this.service.render();
        const { code, data } = await wiz.call("client_status");
        this.clientsLoading = false;
        if (code === 200) {
            this.clients = (data || []).map((c: any) => ({ ...c, selected: false }));
        }
        await this.service.render();
    }

    public toggleSelectAll() {
        this.selectAll = !this.selectAll;
        this.clients.forEach(c => c.selected = this.selectAll);
        this.service.render();
    }

    public get selectedClients() {
        return this.clients.filter(c => c.selected);
    }

    public async runBulkAction(action: string) {
        const selected = this.selectedClients;
        if (selected.length === 0) return;
        if (!confirm(`Run "${action}" on ${selected.length} node(s)?`)) return;
        this.bulkRunning = true;
        this.bulkResults = [];
        await this.service.render();
        const nodeIds = selected.map(c => c.id);
        const { code, data } = await wiz.call("bulk_action", {
            action: action,
            node_ids: JSON.stringify(nodeIds),
        });
        this.bulkRunning = false;
        if (code === 200) {
            this.bulkResults = data || [];
        }
        await this.loadClients();
        await this.service.render();
    }

    public formatBytes(bytes: number): string {
        if (!bytes || bytes <= 0) return '0 B';
        const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + units[i];
    }

    public getUsagePercent(used: number, total: number): number {
        if (!total) return 0;
        return Math.round((used / total) * 100);
    }

    public getUsageColor(percent: number): string {
        if (percent >= 90) return 'bg-red-500';
        if (percent >= 75) return 'bg-amber-500';
        return 'bg-emerald-500';
    }

    public getStatusBadge(status: string): string {
        switch (status) {
            case 'active': case 'mounted': case 'online': return 'bg-green-100 text-green-700';
            case 'degraded': return 'bg-amber-100 text-amber-700';
            case 'down': case 'error': case 'unmounted': return 'bg-red-100 text-red-700';
            default: return 'bg-zinc-100 text-zinc-600';
        }
    }
}

export default PageKieraLustreComponent;
